import { useEffect, useState } from 'react';
import {
  PrefReviewSelector,
  type TTEPreferencesViewLink,
  type TViewLinkSubmitter,
  getViewLink,
  updatePrefReview,
} from 'slices/appsPrefReviewSettings';
import { organizationSelector } from 'slices/organization.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  type ViewerOptions,
  requestViewerPages,
  viewerPagesSelector,
} from 'slices/viewerPages.slice';
import { EAppId, getRedirectUri } from '@timeedit/types';
import { EAppEnv } from '@timeedit/types/lib/enums/appEnv.enum';
import { Alert, Button, DefaultContentLayout } from '@timeedit/ui-components';
import {
  Col,
  Input,
  Row,
  Select,
  Typography,
} from '@timeedit/ui-components/antd';
import intl from '../../../i18n/intl';
import './AllocateSettings.scss';

const language: any = intl.messages;

export const filterOptionLowerCaseInclude = (
  input: string,
  option?: { label?: string }
) => {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
};

const AllocateViewerPage = () => {
  const dispatch = useAppDispatch();
  const organization = useAppSelector(organizationSelector);
  const organizationDetails = useAppSelector(PrefReviewSelector);
  const pages = useAppSelector(viewerPagesSelector);
  const appEnv =
    (import.meta.env.VITE_TE_APP_ENV as EAppEnv) ?? EAppEnv.PRODUCTION;

  const [form, setForm] = useState<TTEPreferencesViewLink>({
    // TODO: We need validation for this field since the BE expects a boolean one of the typed values
    viewLinkSubmitter: '' as TViewLinkSubmitter,
    viewerEntry: '',
    viewerPath: '',
    viewerPage: '',
    commentField: '',
  });

  const [pageValue, setPageValue] = useState<string>(null);
  const [link, setLink] = useState<string>();

  useEffect(() => {
    if (!form.viewerPath || !form.viewerPage) {
      setPageValue(null);
      return;
    }
    setPageValue(`${form.viewerPath}${form.viewerPage}`);
  }, [pages, form]);

  useEffect(() => {
    dispatch(getViewLink());
    dispatch(requestViewerPages(organization?.customerSignature));
  }, [dispatch]);

  useEffect(() => {
    setForm(organizationDetails);
  }, [organizationDetails]);

  useEffect(() => {
    setLink(linkToViewer());
  }, [form]);

  const _handleSubmit = () => {
    dispatch(updatePrefReview({ ...form }));
  };

  function linkToViewer(): string {
    if (!form.viewerPath) {
      return '';
    }
    if (!form.viewerPage) {
      return '';
    }
    const base = getRedirectUri(
      EAppId.TE_VIEWER,
      organization?.customerSignature,
      appEnv
    );
    return `${base}/${form.viewerPath}/r.html?h=t&sid=${form.viewerPage}`;
  }

  function presentLink() {
    if (!link) {
      return 'Link disabled';
    }
    return (
      <span>
        Viewer link{' '}
        <a href={link} target="_blank" rel="noreferrer">
          {link}
        </a>
      </span>
    );
  }

  return (
    <DefaultContentLayout
      title={language.manage_viewer_page}
      actions={
        <Button type="primary" onClick={_handleSubmit}>
          {language.save_changes as string}
        </Button>
      }
    >
      <div className="allocate-settings-container">
        <div className="content-box">
          <Typography.Title className="row-box" level={4}>
            {'Link to Viewer'}
          </Typography.Title>
          <p className="row-box">{presentLink()}</p>
          <Row align="middle" className="row-box">
            <Col>{'Page'}&nbsp; </Col>
            <Col className="space-after">
              <Select<any, ViewerOptions>
                className="page-dropdown"
                showSearch
                placeholder="Select a page"
                onChange={(value, option) => {
                  setPageValue(value);
                  if (!option) {
                    return;
                  }
                  setForm({
                    ...form,
                    viewerPath: `${option['path']}`,
                    viewerPage: `${option['id']}`,
                  });
                }}
                filterOption={filterOptionLowerCaseInclude}
                options={pages}
                value={pageValue}
                popupMatchSelectWidth={false}
              />
            </Col>
            <Col>
              <Input
                size="small"
                variant="filled"
                value={form.viewerPath}
                onChange={(e: any) =>
                  setForm({ ...form, viewerPath: e.target.value })
                }
              />
            </Col>
            <Col>
              <Input
                size="small"
                variant="filled"
                value={form.viewerPage}
                onChange={(e: any) =>
                  setForm({ ...form, viewerPage: e.target.value })
                }
              />
            </Col>
          </Row>

          <Alert
            className="help-text"
            description={language.allocateViewerLinkHelp}
            type="info"
            showIcon={false}
          />
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default AllocateViewerPage;
